import {
  getBidderListApi,
  getBidderSummaryApi,
  getDecodeResultApi,
  returnBidFileApi
} from '@/api/hall/bid-open-hall'
import {
  getToBeQuoteDecryptedPageApi,
  getToBeQuoteDecryptSummaryApi,
  rejectQuoteOpenFileApi
} from '@/api/hall/quote-open'
import { fileDecryptionStatusDict } from '@/dictionary/hall/open-bid-hall'
import { workbenchServiceDict } from '@/dictionary/system'
import { bidFileTypeDict } from '@/dictionary/project/kpb-project'

/**
 * 采购人/代理机构文件解密服务
 */
const decryptFileByPurchaser = (projectId, bidFileType) => {
  /**
   * 查询业务端文件解密结果
   */
  const getDecryptResult = (bidderId, encryptFileId) => {
    return new Promise((resolve, reject) => {
      getDecodeResultApi(
        {
          projectId: projectId,
          bidderId: bidderId,
          encryptFileId: encryptFileId
        },
        {
          bidFileType: bidFileType
        }
      )
        .then(res => {
          const decryptStatus =
            res?.data?.status || fileDecryptionStatusDict.PENDING.value
          resolve(decryptStatus)
        })
        .catch(err => reject(err))
    })
  }

  const serviceMap = {
    [bidFileTypeDict.BUSINESS.value]: {
      /**
       * 分页查询商务加密文件列表
       */
      getBidEncryptFileListPage: params => {
        return new Promise((resolve, reject) => {
          getBidderListApi(projectId, params)
            .then(res => {
              const records = res.data?.records || []
              const total = res.data?.total || 0
              resolve({ records, total })
            })
            .catch(err => reject(err))
        })
      },
      /**
       * 获取商务加密文件统计信息
       */
      getBidEncryptFileStatistics: () => {
        return new Promise((resolve, reject) => {
          getBidderSummaryApi(projectId)
            .then(res => {
              const data = res.data || {}
              const allCount = data.allCount || 0
              const waitCount = data.fileWaitDecryptCount || 0
              resolve({ allCount, waitCount })
            })
            .catch(err => reject(err))
        })
      },
      /**
       * 退回商务文件
       * @param {Number} bidderId 投标单位id
       * @param {Object} form 发送的数据
       * @param {String} form.backRemark 退回原因
       * @returns {Promise<void>|*}
       */
      rejectBidFile: (bidderId, form) => {
        return returnBidFileApi(projectId, bidderId, form)
      }
    },
    [bidFileTypeDict.QUOTATION.value]: {
      /**
       * 分页查询报价加密文件列表
       */
      getQuoteEncryptFilePage: params => {
        return new Promise((resolve, reject) => {
          getToBeQuoteDecryptedPageApi(projectId, params)
            .then(res => {
              const records = res.data?.records || []
              const total = res.data?.total || 0
              resolve({ records, total })
            })
            .catch(err => reject(err))
        })
      },

      /**
       * 获取报价加密文件统计信息
       */
      getQuoteEncryptFileStatistics: () => {
        return new Promise((resolve, reject) => {
          getToBeQuoteDecryptSummaryApi(projectId)
            .then(res => {
              const data = res.data || {}
              const allCount = data.allCount || 0
              const waitCount = data.fileWaitDecryptCount || 0
              resolve({ allCount, waitCount })
            })
            .catch(err => reject(err))
        })
      },

      /**
       * 退回报价文件
       * @param {Number} bidderId 投标单位id
       * @param {Object} form 发送的数据
       * @param {String} form.backRemark 退回原因
       * @returns {Promise<void>|*}
       */
      rejectQuoteFile: (bidderId, form) => {
        return rejectQuoteOpenFileApi(projectId, bidderId, form)
      }
    }
  }

  return {
    ...serviceMap[bidFileType],
    getDecryptResult
  }
}

/**
 * 文件解密服务
 * @param {'purchaser'|'agency'|'supplier'} identity 身份别名（采购人、代理机构、供应商）
 * @param {Number} projectId 项目id
 * @param {Number} bidFileType 文件类型
 * @returns {Object} 相关服务
 */
const useFileDecrypt = (identity, projectId, bidFileType) => {
  const serviceMap = {
    [workbenchServiceDict.PURCHASER.alias]: decryptFileByPurchaser,
    [workbenchServiceDict.AGENCY.alias]: decryptFileByPurchaser
  }
  const serviceFunction = serviceMap[identity]
  return serviceFunction(projectId, bidFileType)
}

export default useFileDecrypt
